import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sorgenteDati } from '@/api/client';
import { getHomePubblica } from '@/features/redazione/apiDashboard';
import { BlocchiRenderer } from '@/features/pagine/BlocchiRenderer';
import type { FasciaHome } from '@/features/redazione/tipi';
import styles from './FasceConfigurate.module.css';

/**
 * Le fasce della home configurate dalla redazione (decisione 82).
 * ---------------------------------------------------------------------------
 * `GET /api/v2/home/` serve solo ciò che è **pubblicato e visibile**, nella
 * lingua richiesta con fallback dichiarato. Elenco vuoto = nessuna
 * configurazione, e **la home resta quella predefinita del frontend**: è
 * un'assenza dichiarata dal contratto, non un errore da segnalare.
 *
 * La fascia di tipo `blocchi` usa lo **stesso registro delle pagine**, quindi
 * lo stesso `BlocchiRenderer`: nessun secondo vocabolario da tenere allineato.
 *
 * **Cosa questo componente NON monta, e lo dice.** I tipi `eventi`, `bandi`,
 * `storie`, `itinerari`, `punti_interesse`, `attivita`, `mappa` e `ricerca`
 * sono fasce che pescano contenuti da altri endpoint con una loro
 * impaginazione: montarle qui significherebbe riscrivere le pagine di
 * elenco dentro la home. Finché non hanno il loro componente, la fascia si
 * dichiara invece di sparire — un contenuto configurato che non compare e
 * non dice niente è la peggiore delle due possibilità.
 */
export function FasceConfigurate({ comune }: { readonly comune?: string }) {
  const { i18n } = useTranslation();
  const lingua = i18n.language || 'it';
  const [fasce, setFasce] = useState<readonly FasciaHome[]>([]);

  useEffect(() => {
    // In modalità fixtures non esiste nessuna configurazione: la chiamata
    // sarebbe un 404 a ogni caricamento della home in sviluppo.
    if (sorgenteDati() !== 'api') return;
    const controller = new AbortController();
    getHomePubblica({ lang: lingua, ...(comune ? { comune } : {}) }, controller.signal)
      .then((risposta) => {
        if (!controller.signal.aborted) setFasce(risposta.risultati);
      })
      .catch(() => {
        // La home non è il posto dove mostrare un errore di rete: resta
        // quella predefinita.
        if (!controller.signal.aborted) setFasce([]);
      });
    return () => controller.abort();
  }, [lingua, comune]);

  if (fasce.length === 0) return null;

  return (
    <>
      {[...fasce]
        .sort((a, b) => a.ordine - b.ordine)
        .map((fascia) => (
          <Fascia key={fascia.chiave} fascia={fascia} />
        ))}
    </>
  );
}

function Fascia({ fascia }: { readonly fascia: FasciaHome }) {
  const { t } = useTranslation();
  const idTitolo = `fascia-${fascia.chiave}`;

  const testata = fascia.titolo ? (
    <>
      <h2 id={idTitolo} className={styles.titolo}>
        {fascia.titolo}
      </h2>
      {fascia.sottotitolo ? <p className={styles.sottotitolo}>{fascia.sottotitolo}</p> : null}
      {/* Decisione 36: il fallback di lingua SI DICE, non si nasconde. */}
      {fascia.tradotta === false ? (
        <p className={styles.avvisoLingua}>
          {t(
            'page_not_translated',
            'Questa sezione non è ancora tradotta: stai leggendo la versione italiana.',
          )}
        </p>
      ) : null}
    </>
  ) : null;

  if (fascia.tipo === 'hero' || fascia.tipo === 'blocchi') {
    return (
      <section
        className={styles.fascia}
        {...(fascia.titolo ? { 'aria-labelledby': idTitolo } : {})}
      >
        <div className="ic-container">
          {testata}
          <BlocchiRenderer blocchi={fascia.blocchi} />
        </div>
      </section>
    );
  }

  // Tipo configurato ma senza componente in questo frontend: si dichiara.
  return (
    <section
      className={styles.fascia}
      {...(fascia.titolo ? { 'aria-labelledby': idTitolo } : {})}
    >
      <div className="ic-container">
        {testata}
        <p role="note" className={styles.nonMontata}>
          {t('home_band_missing', {
            tipo: fascia.tipo,
            defaultValue: `Questa sezione della home (tipo "${fascia.tipo}") è configurata ma non è ancora visualizzabile su questo sito: segnalalo alla redazione.`,
          })}
        </p>
      </div>
    </section>
  );
}
